import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, DollarSign, Clock, CheckCircle, MousePointer, 
  RefreshCw, Filter, ChevronDown, ExternalLink, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
  <Card data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold" style={{ color }}>{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full`} style={{ backgroundColor: `${color}20` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = {
    clicked: { color: 'bg-blue-100 text-blue-800', label: 'Clicked' },
    pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
    paid: { color: 'bg-green-100 text-green-800', label: 'Paid' }
  };
  
  const { color, label } = config[status] || config.clicked;
  
  return (
    <Badge className={`${color} border-0`}>
      {label}
    </Badge>
  );
};

const CommissionsPage = () => {
  const navigate = useNavigate();
  const [clicks, setClicks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [partners, setPartners] = useState([]);
  const [selectedClicks, setSelectedClicks] = useState([]);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (partnerFilter !== 'all') params.append('partner', partnerFilter);
      params.append('limit', '200');

      const [clicksRes, statsRes] = await Promise.all([
        axios.get(`${API}/affiliate-tracking/clicks?${params}`),
        axios.get(`${API}/affiliate-tracking/stats`)
      ]);

      setClicks(clicksRes.data.clicks);
      setStats(statsRes.data);
      
      // Extract unique partners
      const uniquePartners = [...new Set(clicksRes.data.clicks.map(c => c.partner_name))];
      setPartners(uniquePartners);
    } catch (error) {
      console.error('Failed to fetch commission data:', error);
      toast.error('Failed to load commission data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, partnerFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update click status
  const updateStatus = async (clickId, newStatus) => {
    try {
      await axios.put(`${API}/affiliate-tracking/clicks/${clickId}/status`, {
        status: newStatus
      });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  // Bulk update status
  const bulkUpdateStatus = async (newStatus) => {
    if (selectedClicks.length === 0) {
      toast.error('No clicks selected');
      return;
    }
    
    try {
      await axios.put(`${API}/affiliate-tracking/clicks/bulk-status`, {
        click_ids: selectedClicks,
        status: newStatus
      });
      toast.success(`Updated ${selectedClicks.length} clicks to ${newStatus}`);
      setSelectedClicks([]);
      fetchData();
    } catch (error) {
      console.error('Failed to bulk update:', error);
      toast.error('Failed to update clicks');
    }
  };

  // Delete click
  const deleteClick = async (clickId) => {
    if (!confirm('Are you sure you want to delete this click record?')) return;
    
    try {
      await axios.delete(`${API}/affiliate-tracking/clicks/${clickId}`);
      toast.success('Click deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete click:', error);
      toast.error('Failed to delete click');
    }
  };

  // Toggle click selection
  const toggleSelect = (clickId) => {
    setSelectedClicks(prev => 
      prev.includes(clickId) 
        ? prev.filter(id => id !== clickId)
        : [...prev, clickId]
    );
  };

  // Select all visible clicks
  const selectAll = () => {
    if (selectedClicks.length === clicks.length) {
      setSelectedClicks([]);
    } else {
      setSelectedClicks(clicks.map(c => c.click_id));
    }
  };

  // Format date
  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Archivo, sans-serif' }}>
                  Affiliate Commissions
                </h1>
                <p className="text-sm text-muted-foreground">
                  Track clicks and manage 15% commission payouts
                </p>
              </div>
            </div>
            <Button onClick={fetchData} variant="outline" className="gap-2">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats Grid */}
        {stats && stats.overall && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              title="Total Clicks"
              value={stats.overall.total_clicks || 0}
              subtitle={`$${(stats.overall.total_commission || 0).toFixed(2)} total`}
              icon={MousePointer}
              color="#2563EB"
            />
            <StatCard
              title="Pending"
              value={stats.overall.pending_count || 0}
              subtitle={`$${(stats.overall.pending_commission || 0).toFixed(2)}`}
              icon={Clock}
              color="#EAB308"
            />
            <StatCard
              title="Paid"
              value={stats.overall.paid_count || 0}
              subtitle={`$${(stats.overall.paid_commission || 0).toFixed(2)}`}
              icon={CheckCircle}
              color="#22C55E"
            />
            <StatCard
              title="Commission Rate"
              value="15%"
              subtitle="Per affiliate sale"
              icon={DollarSign}
              color="#F97316"
            />
          </div>
        )}

        {/* Partner Stats */}
        {stats?.by_partner?.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-lg">Commission by Partner</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.by_partner.map((partner) => (
                  <div key={partner.partner_name} className="p-3 bg-muted/30 rounded-lg">
                    <p className="font-medium text-sm">{partner.partner_name}</p>
                    <p className="text-lg font-bold">${(partner.total_commission || 0).toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">
                      {partner.total_clicks || 0} clicks · {partner.paid_count || 0} paid
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters & Actions */}
        <div className="flex flex-wrap gap-4 items-center justify-between mb-6">
          <div className="flex gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="clicked">Clicked</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>

            <Select value={partnerFilter} onValueChange={setPartnerFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Partner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Partners</SelectItem>
                {partners.map(p => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedClicks.length > 0 && (
            <div className="flex gap-2">
              <span className="text-sm text-muted-foreground py-2">
                {selectedClicks.length} selected
              </span>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => bulkUpdateStatus('pending')}
              >
                Mark Pending
              </Button>
              <Button 
                size="sm" 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => bulkUpdateStatus('paid')}
              >
                Mark Paid
              </Button>
            </div>
          )}
        </div>

        {/* Clicks Table */}
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <input 
                    type="checkbox" 
                    checked={selectedClicks.length === clicks.length && clicks.length > 0}
                    onChange={selectAll}
                    className="rounded"
                  />
                </TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Partner</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead className="text-right">Commission (15%)</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : clicks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No affiliate clicks recorded yet
                  </TableCell>
                </TableRow>
              ) : (
                clicks.map((click) => (
                  <TableRow key={click.click_id} data-testid={`click-row-${click.click_id}`}>
                    <TableCell>
                      <input 
                        type="checkbox" 
                        checked={selectedClicks.includes(click.click_id)}
                        onChange={() => toggleSelect(click.click_id)}
                        className="rounded"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="max-w-[200px]">
                        <p className="font-medium text-sm truncate">{click.product_title}</p>
                        <p className="text-xs text-muted-foreground truncate">{click.click_id}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{click.partner_name}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      ${(click.product_price || 0).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold text-green-600">
                      ${(click.commission || 0).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={click.status} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(click.timestamp)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {click.status !== 'pending' && (
                            <DropdownMenuItem onClick={() => updateStatus(click.click_id, 'pending')}>
                              <Clock className="w-4 h-4 mr-2" />
                              Mark Pending
                            </DropdownMenuItem>
                          )}
                          {click.status !== 'paid' && (
                            <DropdownMenuItem onClick={() => updateStatus(click.click_id, 'paid')}>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Mark Paid
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            onClick={() => window.open(click.partner_url, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            View Partner URL
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => deleteClick(click.click_id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Summary Footer */}
        {clicks.length > 0 && (
          <div className="mt-6 p-4 bg-muted/30 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                Showing {clicks.length} clicks
              </span>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Total Commission (visible)</p>
                <p className="text-xl font-bold text-green-600">
                  ${clicks.reduce((sum, c) => sum + (c.commission || 0), 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CommissionsPage;
